import React, { useEffect, useMemo, useState } from 'react';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import CircularProgress from '@mui/material/CircularProgress';
import Pagination from '@mui/material/Pagination';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';

// DataGrid กลางของทั้งแอป: ปรับความกว้างคอลัมน์ตามข้อมูล แล้วขยายให้เต็มจอ + สไตล์เดียวกันทุกหน้า
const autosizeOptions = {
    includeHeaders: true,
    includeOutliers: true,
    expand: true,
};

const baseSx = {
    backgroundColor: '#fff',
    border: '1px solid #e3e6ea',
    borderRadius: 2,
    fontSize: 14,
    '--DataGrid-containerBackground': '#f4f6f9',
    '& .MuiDataGrid-columnHeaderTitle': {
        fontWeight: 600,
        color: '#37474f',
    },
    '& .MuiDataGrid-row.row-odd': {
        backgroundColor: '#fafbfc',
    },
    '& .MuiDataGrid-row:hover': {
        backgroundColor: '#eef4ff',
    },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
        outline: 'none',
    },
    '& .MuiDataGrid-footerContainer': {
        borderTop: '1px solid #e3e6ea',
    },
    // แถบเครื่องมือ (Columns / Filters / Density / Export)
    '& .MuiDataGrid-toolbarContainer': {
        gap: 1,
        px: 1.5,
        py: 1.25,
        borderBottom: '1px solid #e3e6ea',
    },
    '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
        borderRadius: 999,
        px: 1.5,
        py: 0.5,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: 13,
        color: '#455a64',
        backgroundColor: '#f4f6f9',
        border: '1px solid #e3e6ea',
        '&:hover': {
            backgroundColor: '#eef4ff',
            borderColor: '#90caf9',
            color: '#1e88e5',
        },
    },
};

const CARD_PAGE_SIZE = 12;

const isActionsColumn = (col) => col.type === 'actions' || col.field === 'actions';
const isStatusColumn = (col) => /status/i.test(col.field) || /status/i.test(col.headerName || '');

// แสดงค่าของคอลัมน์ในโหมด Card ให้เหมือนในตาราง (renderCell / valueGetter / valueFormatter)
function renderColumnValue(col, row, id, apiRef) {
    const rawValue = row[col.field];
    const value = col.valueGetter ? col.valueGetter(rawValue, row, col, apiRef) : rawValue;
    if (col.renderCell) {
        return col.renderCell({ id, row, field: col.field, value, formattedValue: value, colDef: col, api: apiRef.current, rowNode: { id }, hasFocus: false, tabIndex: -1 });
    }
    const formatted = col.valueFormatter ? col.valueFormatter(value, row, col, apiRef) : value;
    return formatted === null || formatted === undefined || formatted === '' ? '-' : String(formatted);
}

// โหมด Card: หัวการ์ด = คอลัมน์แรก, มุมขวา = คอลัมน์สถานะ, ปุ่ม Actions อยู่ท้ายการ์ด
function CardView({ rows, columns, getRowId, apiRef, loading, renderCard }) {
    const [page, setPage] = useState(1);

    useEffect(() => {
        setPage(1);
    }, [rows]);

    const actionsCol = columns.find(isActionsColumn);
    const dataCols = columns.filter((col) => !isActionsColumn(col));
    const statusCol = dataCols.find(isStatusColumn);
    const [titleCol, ...restCols] = dataCols.filter((col) => col !== statusCol);
    const pageRows = rows.slice((page - 1) * CARD_PAGE_SIZE, page * CARD_PAGE_SIZE);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={32} /></Box>;
    }
    if (rows.length === 0) {
        return <Box sx={{ textAlign: 'center', py: 6, color: '#90a4ae', bgcolor: '#fff', border: '1px solid #e3e6ea', borderRadius: 2 }}>No rows</Box>;
    }

    return (
        <Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
                {pageRows.map((row, index) => {
                    const id = getRowId ? getRowId(row) : row.id ?? index;
                    if (renderCard) return <React.Fragment key={id}>{renderCard(row)}</React.Fragment>;
                    return (
                        <Card key={id} variant="outlined" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', borderColor: '#e3e6ea', borderRadius: 2 }}>
                            <CardContent sx={{ pb: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                    <Box sx={{ fontWeight: 700, fontSize: 16, color: '#37474f', wordBreak: 'break-word' }}>
                                        {titleCol && renderColumnValue(titleCol, row, id, apiRef)}
                                    </Box>
                                    {statusCol && <Box sx={{ flexShrink: 0 }}>{renderColumnValue(statusCol, row, id, apiRef)}</Box>}
                                </Box>
                                <Box sx={{ fontSize: 13, color: '#6b6b6b', lineHeight: 1.7 }}>
                                    {restCols.map((col) => (
                                        <Box key={col.field} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <Box component="span" sx={{ color: '#90a4ae', flexShrink: 0 }}>{col.headerName || col.field}:</Box>
                                            <Box component="span" sx={{ color: '#37474f', minWidth: 0, wordBreak: 'break-word' }}>
                                                {renderColumnValue(col, row, id, apiRef)}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </CardContent>
                            {actionsCol &&
                                <CardActions sx={{ borderTop: '1px solid #eee' }}>
                                    {renderColumnValue(actionsCol, row, id, apiRef)}
                                </CardActions>}
                        </Card>
                    );
                })}
            </Box>
            {rows.length > CARD_PAGE_SIZE &&
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                        count={Math.ceil(rows.length / CARD_PAGE_SIZE)}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        color="primary"
                    />
                </Box>}
        </Box>
    );
}

// viewToggle: ปุ่มสลับ Table / Card (ปิดอัตโนมัติสำหรับตารางที่มี checkbox หรือแก้ไขในตารางได้)
// renderCard: ใช้การ์ดแบบกำหนดเองแทนการ์ดมาตรฐาน
function AppDataGrid({ viewToggle, renderCard, viewStorageKey, ...gridProps }) {
    const canUseCards = !gridProps.checkboxSelection && !gridProps.columns.some((col) => col.editable);
    const showToggle = viewToggle ?? canUseCards;
    const storageKey = viewStorageKey || `gridViewMode:${window.location.pathname}`;

    const [viewMode, setViewMode] = useState(() => {
        try { return localStorage.getItem(storageKey) || 'table' } catch (e) { return 'table' }
    });

    const changeViewMode = (event, mode) => {
        if (!mode) return;
        setViewMode(mode);
        try { localStorage.setItem(storageKey, mode) } catch (e) { }
    };

    const internalApiRef = useGridApiRef();
    const apiRef = gridProps.apiRef || internalApiRef;

    if (!showToggle) return <BaseDataGrid {...gridProps} />;

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <ToggleButtonGroup value={viewMode} exclusive size="small" onChange={changeViewMode} aria-label="view mode" sx={{ bgcolor: '#fff' }}>
                    <ToggleButton value="table" aria-label="table view">
                        <Tooltip title="Table"><ViewListIcon /></Tooltip>
                    </ToggleButton>
                    <ToggleButton value="card" aria-label="card view">
                        <Tooltip title="Card"><ViewModuleIcon /></Tooltip>
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>
            {viewMode === 'card'
                ? <CardView rows={gridProps.rows || []} columns={gridProps.columns} getRowId={gridProps.getRowId} apiRef={apiRef} loading={gridProps.loading} renderCard={renderCard} />
                : <BaseDataGrid {...gridProps} apiRef={apiRef} />}
        </Box>
    );
}

function BaseDataGrid({ apiRef: externalApiRef, columns, rows, sx, getRowClassName, ...props }) {
    const internalApiRef = useGridApiRef();
    const apiRef = externalApiRef || internalApiRef;

    // จำกัดความกว้างไม่ให้คอลัมน์ข้อความยาวกินพื้นที่ทั้งตาราง (คอลัมน์ไหนกำหนดเองไว้จะใช้ค่าของคอลัมน์นั้น)
    const sizedColumns = useMemo(
        () => columns.map((col) => ({ minWidth: 60, maxWidth: 400, ...col })),
        [columns]
    );

    useEffect(() => {
        const timer = setTimeout(() => {
            apiRef.current?.autosizeColumns?.(autosizeOptions);
        }, 0);
        return () => clearTimeout(timer);
    }, [apiRef, rows, sizedColumns]);

    useEffect(() => {
        let timer;
        const onResize = () => {
            clearTimeout(timer);
            timer = setTimeout(() => apiRef.current?.autosizeColumns?.(autosizeOptions), 200);
        };
        window.addEventListener('resize', onResize);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', onResize);
        };
    }, [apiRef]);

    return (
        <DataGrid
            apiRef={apiRef}
            rows={rows}
            columns={sizedColumns}
            autosizeOnMount
            autosizeOptions={autosizeOptions}
            getRowClassName={(params) =>
                [params.indexRelativeToCurrentPage % 2 === 1 ? 'row-odd' : '', getRowClassName ? getRowClassName(params) : '']
                    .filter(Boolean)
                    .join(' ')
            }
            sx={[baseSx, ...(Array.isArray(sx) ? sx : [sx])].filter(Boolean)}
            // ค่าเริ่มต้นแสดง 25 รายการต่อหน้า (หน้าไหนส่ง initialState/pageSizeOptions มาเองจะใช้ค่าของหน้านั้น)
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[10, 25, 50, 100]}
            {...props}
        />
    );
}

export default AppDataGrid;
